import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Template} from '@mintdata-wsp/common/state/DataState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {GrabDesignButton} from '@mintdata-wsp/wsp/ui/views/workspace/home/template/GrabDesignButton';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./TemplateAppPreview.less');
const cx = classnames.bind(styles) as any;

enum ViewportSize
{
    Desktop = 'desktopSize',
    Tablet = 'tabletSize',
    Phone = 'phoneSize'
}

interface Props
{
    template:Template;
}

interface State
{
    activeViewportSize:ViewportSize;
    isFullView:boolean;
    isFullText:boolean;

    animationInProgress:boolean;

    isDesignOpened:boolean;
    isDesignOpenedOnce:boolean;
}

@observer
export class TemplateAppPreview extends React.Component<Props, State>
{
    public constructor(props:any)
    {
        super(props);

        const {template} = this.props;

        this.state = {
            activeViewportSize: this._getViewportSize(template),
            isFullView: false,
            isFullText: false,
            animationInProgress: true,
            isDesignOpened: false,
            isDesignOpenedOnce: false,
        };

        window.onhashchange = this._locationHashChanged;
    }

    componentDidUpdate(prevProps:Readonly<Props>, prevState:Readonly<State>, prevContext:any)
    {
        const {template} = this.props;
        const {template: prevTemplate} = prevProps;

        if( (!prevTemplate && template && template.publishedAppDevice) ||
            (prevTemplate && template && template.publishedAppDevice !== prevTemplate.publishedAppDevice) )
        {
            this.setState({activeViewportSize: this._getViewportSize(template)});
        }
    }

    private _getViewportSize(template:Template):ViewportSize
    {
        let activeViewportSize = ViewportSize.Tablet;

        if( !template || !template.publishedAppDevice ) return activeViewportSize;

        const {publishedAppDevice} = template;

        switch( publishedAppDevice.toLowerCase() )
        {
            case 'desktop':
                activeViewportSize = ViewportSize.Desktop;
                break;
            case 'mobile':
                activeViewportSize = ViewportSize.Phone;
                break;
        }

        return activeViewportSize;
    }

    private _locationHashChanged = () =>
    {
        const {isFullView} = this.state;

        const {hash} = window.location;

        if( hash === '#preview' )
        {
            this.setState({isFullView: true});
        }

        if( isFullView && (hash !== '#preview') )
        {
            this.setState({isFullView: false});
        }
    };

    private setLocationHash = (hash:string) =>
    {
        window.location.hash = hash;
    };

    private _onViewportSizeClick(viewportSize:ViewportSize, event:React.MouseEvent<HTMLElement>):void
    {
        this.setState({
            activeViewportSize: viewportSize
        });
    }

    private _getDesignId():number
    {
        const {designId, devDesignId} = this.props.template;

        const isProd = ConfigUtil.isProd();

        return isProd || !devDesignId ? designId : devDesignId;
    }

    private _onClickBtnPlus = () =>
    {
        this.setState({isFullView: true});
        this.setLocationHash('preview');
    };

    private _onClickBtnBack = (e:React.MouseEvent<HTMLElement>) =>
    {
        e.preventDefault();

        const {isFullView} = this.state;

        if( isFullView )
        {
            window.history.back();
        }
        else
        {
            controllers.navigation.navigateTo(paths.templates());
        }
    };

    private _onClickBtnDown = (e:React.MouseEvent<HTMLElement>) =>
    {
        e.preventDefault();

        const {isFullText} = this.state;

        this.setState({isFullText: !isFullText});
    };

    private _onLoadIframe = () =>
    {
        this.setState({animationInProgress: false});
    };

    public render():JSX.Element
    {
        return (
            <div className={cx('liveDemo')}>
                {this._renderHeader()}
                {this._renderAppView()}
            </div>
        );
    }

    private _renderHeader():JSX.Element
    {
        const {template} = this.props;

        if( !template ) return null;

        const {activeViewportSize, isFullView} = this.state;

        return (
            <div className={styles.HeaderBlock}>
                <div className={styles.HeaderBlockContent}>

                    <div className={styles.title}>
                        <a
                            href={isFullView ? '#' : paths.templates()}
                            className={styles.back}
                            onClick={this._onClickBtnBack}
                        >
                            <MDIcon
                                className={styles.iconBack}
                                name={MDIcons.common.ChevronRight}
                                size={MDIconSize.large}
                            />
                            <span className={styles.backText}>
                                Back
                            </span>
                        </a>
                    </div>

                    <div className={cx(styles.viewportSizes, {isFullView})}>

                        <button
                            className={cx('viewportSizeBtn', {active: activeViewportSize === ViewportSize.Desktop})}
                            onClick={this._onViewportSizeClick.bind(this, ViewportSize.Desktop)}
                        >
                            <MDIcon name={MDIcons.common.ViewportDesktop} />
                        </button>
                        <button
                            className={cx('viewportSizeBtn', {active: activeViewportSize === ViewportSize.Tablet})}
                            onClick={this._onViewportSizeClick.bind(this, ViewportSize.Tablet)}
                        >
                            <MDIcon name={MDIcons.common.ViewportTablet} />
                        </button>
                        <button
                            className={cx('viewportSizeBtn', {active: activeViewportSize === ViewportSize.Phone})}
                            onClick={this._onViewportSizeClick.bind(this, ViewportSize.Phone)}
                        >
                            <MDIcon name={MDIcons.common.ViewportPhone} />
                        </button>

                    </div>

                    <div className={styles.btnsWrapperDesktop}>
                        {this._renderGrabDesignButton(styles.headerGrabBtn)}
                    </div>

                </div>
            </div>
        );
    }

    private _renderAppView():JSX.Element
    {
        const {template} = this.props;

        if( !template ) return null;

        const {
            description,
            publishedAppSubdomain,
        } = template;

        const {activeViewportSize, isFullView, isFullText} = this.state;

        return (
            <div className={cx(styles.demoWrapper, {isFullView})}>

                <img
                    className={styles.bgImg}
                    src="/assets/images/home/template/bg-gray-blocks.svg"
                    alt="background image"
                />

                <div className={styles.descriptionWrapper}>
                    <div className={styles.description}>
                        <div
                            className={cx(styles.descriptionText, {isFullText})}
                            dangerouslySetInnerHTML={{__html: description}}
                        />
                    </div>
                </div>

                <button
                    className={cx(styles.btnDown, {isFullText})}
                    onClick={this._onClickBtnDown}
                >
                    <MDIcon
                        className={styles.iconDown}
                        name={MDIcons.common.ChevronRight}
                        size={MDIconSize.large}
                    />
                </button>

                <div className={styles.btnsWrapperMobile}>

                    {this._renderGrabDesignButton(styles.headerGrabBtnMobile)}

                </div>

                <div className={cx(styles.appWrapper, activeViewportSize)}>
                    <div className={styles.iframeWrapper}>
                        <div
                            className={styles.iframe}
                            onClick={this._onClickBtnPlus}
                        >
                            <iframe
                                // loading="lazy"
                                src={`https://${publishedAppSubdomain}.on-mintdata.com`}
                                onLoad={this._onLoadIframe}
                            />

                            {this._renderThumbnails()}

                        </div>
                    </div>
                </div>

                <div
                    className={styles.btnPlus}
                    onClick={this._onClickBtnPlus}
                >
                    <img
                        className={cx(styles.btnPlusImg)}
                        src="/assets/images/home/template/preview-live-demo-button.svg"
                        alt="Preview live demo button"
                    />
                </div>

                <img
                    className={cx('lazy', styles.imgCheckOutArrow)}
                    src="/assets/images/home/template/check-out-live-demo-arrow.svg"
                    alt="Check out the live demo"
                />

            </div>
        );
    }

    private _renderThumbnails():JSX.Element | null
    {
        const {animationInProgress} = this.state;

        if( !animationInProgress ) return null;

        const {template} = this.props;

        if( !template ) return null;

        const {thumbnailUrl, thumbnailUrlWebP, shortTitle} = template;

        return (
            <div className={styles.thumbnailsWrapper}>
                <div className={styles.thumbnails}>
                    {
                        thumbnailUrl.map((url:string, index:number) =>
                        {
                            const webpUrl = thumbnailUrlWebP && thumbnailUrlWebP[index];

                            return (
                                <div
                                    key={index}
                                    className={styles.thumbnail}
                                >
                                    <picture>
                                        <source srcSet={`${webpUrl} 1x`} type="image/webp" />
                                        <img
                                            src={url}
                                            className={styles.thumbnailImage}
                                            alt={`${shortTitle} - screenshot ${index + 1}`}
                                        />
                                    </picture>
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        );
    }

    private _renderGrabDesignButton(className:any):JSX.Element
    {
        const {template} = this.props;

        if( !template ) return null;

        return (
            <GrabDesignButton
                designId={this._getDesignId()}
            />
        );
    }
}
